import { TimeSlot } from "./types";

export interface PickupDatetimeConfig {
  startHour: number;
  endHour: number;
  leadTimeHours: number;
}

const DEFAULT_CONFIG: PickupDatetimeConfig = {
  startHour: 8,
  endHour: 23,
  leadTimeHours: 4,
};

const HOUR_INCREMENT = 1;

function roundUpToNextHour(date: Date): Date {
  const rounded = new Date(date);
  if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  }
  return rounded;
}

function formatTimeLabel(hour: number): string {
  const start = `${String(hour).padStart(2, "0")}.00`;
  const end = `${String(hour + 1).padStart(2, "0")}.00`;
  return `${start} - ${end}`;
}

function formatTimeValue(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

function resolveConfig(config?: Partial<PickupDatetimeConfig>): PickupDatetimeConfig {
  return { ...DEFAULT_CONFIG, ...config };
}

export function getEligibleTimeSlots(selectedDate: Date, config?: Partial<PickupDatetimeConfig>): TimeSlot[] {
  const cfg = resolveConfig(config);
  const now = new Date();
  now.setSeconds(0, 0);
  const isToday =
    selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate();

  let minHour = cfg.startHour;
  if (isToday) {
    const earliest = new Date(
      now.getTime() + cfg.leadTimeHours * 60 * 60 * 1000
    );
    const rounded = roundUpToNextHour(earliest);
    const crossesMidnight =
      rounded.getFullYear() !== now.getFullYear() ||
      rounded.getMonth() !== now.getMonth() ||
      rounded.getDate() !== now.getDate();
    if (crossesMidnight) {
      minHour = cfg.endHour;
    } else {
      minHour = Math.max(cfg.startHour, rounded.getHours());
    }
  }

  const slots: TimeSlot[] = [];
  for (let hour = cfg.startHour; hour < cfg.endHour; hour += HOUR_INCREMENT) {
    slots.push({
      label: formatTimeLabel(hour),
      value: formatTimeValue(hour),
      disabled: isToday && hour < minHour,
    });
  }
  return slots;
}

export function getDefaultTimeSlot(selectedDate: Date, config?: Partial<PickupDatetimeConfig>): string | null {
  const slots = getEligibleTimeSlots(selectedDate, config);
  const firstEnabled = slots.find((s) => !s.disabled);
  return firstEnabled ? firstEnabled.value : null;
}

export function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function getTimeSlotLabel(timeValue: string): string {
  if (!timeValue) return "";
  const hour = parseInt(timeValue.split(":")[0], 10);
  return formatTimeLabel(hour);
}
